import { Component, Input, Output, EventEmitter, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
 
 @Component({
   selector: 'app-pagination',
   standalone: true,
   imports: [CommonModule, FormsModule],
   templateUrl: './pagination.component.html',
   styleUrl: './pagination.component.css'
 })
 export class PaginationComponent {
   @Input() set page(value: number) {
     this._page.set(value);
   }
   get page(): number {
     return this._page();
   }
   
   @Input() set totalPages(value: number) {
     this._totalPages.set(value);
   }
   get totalPages(): number {
     return this._totalPages();
   }
   
   @Input() pageSize: number = 10;
 
   @Output() pageSizeChange = new EventEmitter<number>();
   @Output() pageChange = new EventEmitter<number>();
 
   pageSizeOptions = [5, 10, 20, 50, 100]; // Customize as needed
 
   private _page = signal<number>(1);
   private _totalPages = signal<number>(1);
 
   // Calculate which page numbers to display
   displayPages = computed(() => {
     const currentPage = this._page();
     const totalPages = this._totalPages();
     
     if (totalPages <= 7) {
       // If 7 or fewer pages, show all
       return Array.from({ length: totalPages }, (_, i) => i + 1);
     }
     
     const pages: (number | string)[] = [];
     const delta = 2; // Number of pages to show on each side of current page
     
     // Always show first page
     pages.push(1);
     
     let startPage = Math.max(2, currentPage - delta);
     let endPage = Math.min(totalPages - 1, currentPage + delta);
     
     // Adjust if we're near the start (show more pages on the right)
     if (currentPage <= delta + 2) {
       endPage = Math.min(delta * 2 + 3, totalPages - 1);
       startPage = 2;
     }
     
     // Adjust if we're near the end (show more pages on the left)
     if (currentPage >= totalPages - delta - 1) {
       startPage = Math.max(2, totalPages - delta * 2 - 1);
       endPage = totalPages - 1;
     }
     
     // Add ellipsis after first page if needed
     if (startPage > 2) {
       pages.push('ellipsis-start');
     }
     
     // Add pages around current page
     for (let i = startPage; i <= endPage; i++) {
       pages.push(i);
     }
     
     // Add ellipsis before last page if needed
     if (endPage < totalPages - 1) {
       pages.push('ellipsis-end');
     }
     
     // Always show last page (if more than 1 page total)
     if (totalPages > 1) {
       pages.push(totalPages);
     }
     
     return pages;
   });
 
   goToPage(p: number) {
     if (p >= 1 && p <= this.totalPages && p !== this.page) {
       this.pageChange.emit(p);
     }
   }
 
   onPageSizeChange(size: number) {
     this.pageSizeChange.emit(size);
   }
   
   isEllipsis(page: number | string): boolean {
     return page === 'ellipsis-start' || page === 'ellipsis-end';
   }
 }
